/**
 * <bdp-video-carousel> — carrossel de vídeos shoppable.
 *
 * - Cada vídeo TOCA só enquanto está visível (IntersectionObserver) e pausa
 *   fora da tela: economiza bateria/dados e evita 10 vídeos rodando juntos.
 * - Som: tudo nasce mudo (exigência dos navegadores pra autoplay). O botão
 *   de som ativa UM vídeo por vez — ligar um, muta os outros.
 * - Barra de progresso fina no topo de cada card (timeupdate).
 * - Setas rolam o trilho um card por vez; escondem nas pontas.
 */
if (!customElements.get('bdp-video-carousel')) {
  customElements.define(
    'bdp-video-carousel',
    class BdpVideoCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.bdp-vcar__track');
        this.videos = [...this.querySelectorAll('video')];
        this.prev = this.querySelector('.bdp-vcar__arrow--prev');
        this.next = this.querySelector('.bdp-vcar__arrow--next');
        if (!this.track || !this.videos.length) return;

        // reprodução só com o card visível
        this.io = new IntersectionObserver(
          (entries) =>
            entries.forEach(({ target, isIntersecting }) => {
              if (isIntersecting) target.play().catch(() => {});
              else target.pause();
            }),
          { threshold: 0.45 }
        );
        this.videos.forEach((v) => {
          v.muted = true;
          this.io.observe(v);
        });

        // progresso + som por card
        this.onTime = (e) => {
          const v = e.target;
          const fill = v.closest('.bdp-vcar__card')?.querySelector('.bdp-vcar__progress-fill');
          if (fill && v.duration) fill.style.width = `${(v.currentTime / v.duration) * 100}%`;
        };
        this.onMuteClick = (e) => {
          const btn = e.target.closest('.bdp-vcar__mute');
          if (!btn) return;
          const video = btn.closest('.bdp-vcar__card').querySelector('video');
          const willUnmute = video.muted;
          // um som por vez: silencia todos, depois libera o escolhido
          this.videos.forEach((v) => {
            v.muted = true;
            this.paintMute(v);
          });
          video.muted = !willUnmute;
          if (willUnmute) video.play().catch(() => {});
          this.paintMute(video);
        };
        this.videos.forEach((v) => v.addEventListener('timeupdate', this.onTime));
        this.addEventListener('click', this.onMuteClick);

        // setas
        this.onArrow = (dir) => {
          const step = (this.track.querySelector('.bdp-vcar__item')?.offsetWidth || 300) + 12;
          this.track.scrollBy({ left: dir * step, behavior: 'smooth' });
        };
        this.prev?.addEventListener('click', () => this.onArrow(-1));
        this.next?.addEventListener('click', () => this.onArrow(1));
        this.onScroll = () => {
          const max = this.track.scrollWidth - this.track.clientWidth;
          if (this.prev) this.prev.hidden = this.track.scrollLeft <= 4;
          if (this.next) this.next.hidden = this.track.scrollLeft >= max - 4;
        };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      disconnectedCallback() {
        this.io?.disconnect();
        this.videos?.forEach((v) => v.removeEventListener('timeupdate', this.onTime));
        this.track?.removeEventListener('scroll', this.onScroll);
      }

      paintMute(video) {
        const btn = video.closest('.bdp-vcar__card')?.querySelector('.bdp-vcar__mute');
        if (!btn) return;
        const unmuted = !video.muted;
        btn.setAttribute('aria-pressed', String(unmuted));
        btn.setAttribute('aria-label', unmuted ? 'Silenciar' : 'Ativar som');
        btn.querySelectorAll('.bdp-vcar__mute-x').forEach((el) => (el.style.display = unmuted ? 'none' : ''));
        const wave = btn.querySelector('.bdp-vcar__mute-wave');
        if (wave) wave.style.display = unmuted ? '' : 'none';
      }
    }
  );
}
